import { create } from 'zustand';
import { usePlatformAuthStore } from '@/features/platform-auth/store';

export type PlatformThemeOption = 'light' | 'dark' | 'system';

/**
 * Platform Console theme.
 *
 * ── WHY A SEPARATE STORE FROM lib/themeStore.ts ─────────────────────────────────────────
 * The chambers theme store persists to `UserPreference.theme` via `/me/preferences`, which is
 * keyed to a `User`. A Platform Admin is a `PlatformAdmin` — a different table, a different JWT
 * issuer, and no `users` row — so it has no `UserPreference` to write to. Reusing that store
 * would send an authenticated chambers request from a console that holds no chambers token.
 *
 * The ARCHITECTURE is deliberately identical (three modes, paint-cache-then-source-of-truth, OS
 * listener), so there is one pattern in the codebase rather than two competing ones.
 *
 * ── PER-ADMIN ISOLATION ─────────────────────────────────────────────────────────────────
 * The key is namespaced by platform-admin id. Two admins sharing a browser keep separate
 * themes, which is the isolation requirement. A single admin on two machines currently gets a
 * per-device theme — closing that needs a `PlatformAdmin.theme` column, deliberately deferred
 * rather than bundled into this work (see the report).
 *
 * ── WHY localStorage AND NOT ONLY REACT STATE ───────────────────────────────────────────
 * The page must colour itself BEFORE React mounts, or every reload flashes the wrong theme.
 * localStorage is the only store readable synchronously at that point.
 */

const KEY_PREFIX = 'jurivoxa-platform-theme';

/** Namespaced per admin so one admin's choice cannot reach another on a shared browser. */
function storageKey(adminId: string | null | undefined): string {
  return adminId ? `${KEY_PREFIX}:${adminId}` : KEY_PREFIX;
}

function readStored(adminId: string | null | undefined): PlatformThemeOption {
  try {
    const raw = localStorage.getItem(storageKey(adminId));
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // Private mode, or site data blocked. Falling back is correct — a preference must never
    // stop the console from rendering.
  }
  return 'system';
}

/**
 * Resolve a mode to an actual appearance and paint it.
 *
 * Tailwind is configured `darkMode: ['class']`, so toggling `.dark` on <html> is what actually
 * flips every token. `color-scheme` is set alongside it so OS-drawn controls — scrollbars,
 * <select> popups, date pickers — follow too; without it they stay light on a dark page.
 */
export function applyPlatformTheme(theme: PlatformThemeOption): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', isDark);
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
}

interface PlatformThemeState {
  theme: PlatformThemeOption;
  setTheme: (t: PlatformThemeOption) => void;
  /** Re-read the stored preference for the admin who just signed in. */
  syncForAdmin: (adminId: string | null | undefined) => void;
}

export const usePlatformThemeStore = create<PlatformThemeState>()((set, get) => ({
  theme: readStored(usePlatformAuthStore.getState().admin?.id),

  setTheme(theme) {
    if (get().theme === theme) return;
    const adminId = usePlatformAuthStore.getState().admin?.id;
    try {
      localStorage.setItem(storageKey(adminId), theme);
    } catch {
      // The choice still applies for this session even if it cannot be persisted.
    }
    set({ theme });
    applyPlatformTheme(theme);
  },

  syncForAdmin(adminId) {
    const stored = readStored(adminId);
    set({ theme: stored });
    applyPlatformTheme(stored);
  },
}));

let listenerBound = false;

/**
 * Paint the stored theme and wire the OS listener. Call once at console boot.
 *
 * The listener is what makes "System" live: when the OS flips at sunset the console follows
 * without a reload. It only repaints while the mode IS 'system' — an explicit Light or Dark
 * choice must win over the OS, which is the whole point of choosing.
 */
export function initPlatformTheme(): void {
  applyPlatformTheme(usePlatformThemeStore.getState().theme);

  if (listenerBound) return;
  listenerBound = true;

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (usePlatformThemeStore.getState().theme === 'system') {
      applyPlatformTheme('system');
    }
  });
}
